import React, {FC, useMemo, useState} from 'react';
import {ErrorBoundary} from 'react-error-boundary';
import styled from 'styled-components';

import {useLocalize} from '../../../../../libs/shared/core/src/helpers/internationalization/internationalizationContext';
import {defineMessage} from '../../../../../libs/shared/core/src/helpers/internationalization/message';
import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {useInitialEffect} from '../../../../../libs/shared/core/src/helpers/react/hooks/useEffectHelpers';
import {DataEventActionsEnum} from '../../../../../libs/shared/core/src/models/tracking/dataEventActions';
import {AnalyticsDataEventObject} from '../../../../../libs/shared/core/src/models/tracking/objectNames/analyticsDataEventObject';
import {getHasKnowledgeBaseArticleReactions} from '../../../../../libs/shared/core/src/store/featureFlags/featureFlagsSelectors';
import {AnalyticsPanelId} from '../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {useCommonAppSelector} from '../../../../../libs/shared/core/src/store/useCommonAppSelector';
import {useUserTheme} from '../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {useAppDispatch, useAppSelector} from '../../../../helpers/appReduxHelpers';
import {
  AnalyticsDataPointPageViewPageTypesEnum,
  AnalyticsDataPointTypesEnum,
  AnalyticsGraphMetricsEnum,
  AnalyticsGraphMetricValue,
  AnalyticsScalarMetricsEnum,
  AnalyticsTableMetricsEnum,
} from '../../../../models/api/analyticsMetricsModel';
import {AnalyticsFilterTypesEnum} from '../../../../models/api/analyticsSavedViewModel';
import {openKnowledgeBaseAnalyticsSidePanelAction} from '../../../../store/panels/analytics/details/analyticsPanelDetailsActions';
import {updateFiltersAction} from '../../../../store/panels/analytics/filters/analyticsPanelFiltersActions';
import {getAnalyticsDatetimes} from '../../../../store/panels/analytics/parameters/analyticsPanelParametersSelectors';
import {AnalyticsParameters} from '../../../../store/panels/analytics/parameters/analyticsParameters';
import {fetchVisibleMetricsForReport} from '../../../../store/panels/analytics/report/analyticsPanelReportActions';
import {
  getAnalyticsMetricIsLoading,
  getAnalyticsReportIsLoading,
  makeAreScalarMetricsEmpty,
  makeGetShouldRenderReportEmptyState,
} from '../../../../store/panels/analytics/report/analyticsPanelReportSelectors';
import {useAnalyticsFilter} from '../../../helpers/hooks/useAnalyticsFilter';
import {useAnalyticsSelectedMetrics} from '../../../helpers/hooks/useAnalyticsSelectedMetrics';
import {reportSectionBaseHeight} from '../../../helpers/reportHelpers/analyticsReportHelpers';
import {ReportElementIdsEnum} from '../../../helpers/reportHelpers/reportTypes';
import {useAnalyticsTrackingFrontendEvent} from '../../../helpers/tracking/useAnalyticsTracking';
import {ChartSerie} from '../../charts/commons/types';
import {ReportLineChart} from '../../reportElements/charts/reportLineChart';
import {ReportKeyMetric} from '../../reportElements/keyMetrics/reportKeyMetric';
import {ReportSection} from '../../reportElements/reportSection/reportSection';
import {ReportSectionEmptyState} from '../../reportElements/reportSection/reportSectionEmptyState';
import {ReportSectionFrame} from '../../reportElements/reportSection/reportSectionFrame';
import {ReportTable} from '../../reportElements/table/reportTable';
import {ReportSidePanel} from '../../reportSidePanel/reportSidePanel';
import {StyledReportGridLayoutDiv, StyledReportGridLayoutItemDiv} from '../../reportViewer/reportGridLayout';
import {HeroMetricSubtitle} from '../../reportViewer/reportHeroMetrics';
import {KnowledgeBaseReportEmptyState} from './knowledgeBaseReportEmptyState';
import {KnowledgeBaseReportErrorBoundary} from './knowledgeBaseReportErrorBoundary';
import {KnowledgeBaseReportHeader} from './knowledgeBaseReportHeader';
import {
  KnowledgeBaseReportSidePanel,
  KnowledgeBaseReportSidePanelSelectedItem,
  KnowledgeBaseReportSidePanelSupportTypes,
} from './knowledgeBaseReportSidePanel';

/*
 * Constants.
 */

const defaultPageViewColumnIds = ['page_name', 'page_view', 'unique_page_view'];
const defaultSearchTermColumnIds = ['search_term', 'num_searches', 'num_results', 'click_through_rate'];
const defaultEngagementSortBy = [
  {
    id: 'page_view',
    desc: true,
  },
  {id: 'unique_page_view', desc: true},
  {
    id: 'num_comments',
    desc: true,
  },
  {
    id: 'num_likes',
    desc: true,
  },
  {
    id: 'num_dislikes',
    desc: true,
  },
];
const defaultSearchSortBy = [
  {
    id: 'num_searches',
    desc: true,
  },
];

/*
 * Props.
 */

interface KnowledgeBaseReportProps {
  panelId: AnalyticsPanelId;
  parameters: AnalyticsParameters;
  isVisible: boolean;
}

/*
 * Style.
 */

const StyledReportViewerWrapperDiv = styled.div`
  overflow: hidden;
  display: grid;
  grid-template-rows: auto 1fr;
  padding: 27px 0 0 0;
`;

const StyledReportGridWrapperDiv = styled.div`
  overflow-y: auto;
  padding: 0 48px 24px 48px;
  margin: 0 -48px;
  background: ${(p) => p.theme.greys.shade20};
`;

const StyledReportGrid = styled.div`
  max-width: 1200px;
  padding: 0 48px;
  margin: 53px auto;
`;

const StyledReportSection = styled(ReportSection)`
  padding: 11px 15px 7px 15px;
`;

const ReportLineChartGridFrame = styled(ReportSectionFrame)`
  padding: 11px 30px 11px 15px;
`;

const TableGridFrame = styled(ReportSectionFrame)`
  padding: 11px 15px 11px 15px;
  min-height: 220px;
`;

/*
 * Component.
 */

export const KnowledgeBaseReport: FC<KnowledgeBaseReportProps> = ({panelId, isVisible}) => {
  const hasKnowledgeBaseArticleReactions = useCommonAppSelector(getHasKnowledgeBaseArticleReactions);
  const engagementTableColumnIds = hasKnowledgeBaseArticleReactions
    ? ['page_name', 'num_likes', 'num_dislikes', 'num_comments', 'page_view', 'unique_page_view']
    : defaultPageViewColumnIds;
  const isLoading = useAppSelector(getAnalyticsReportIsLoading);
  const localize = useLocalize();
  const theme = useUserTheme();
  const dispatch = useAppDispatch();
  const getPanelId = useMemo(() => () => panelId, [panelId]);
  const trackAnalyticsFrontendEvent = useAnalyticsTrackingFrontendEvent(getPanelId);

  const [selectedKnowledgeBaseIds] = useAnalyticsFilter(AnalyticsFilterTypesEnum.KNOWLEDGE_BASES);
  const [selectedLocales] = useAnalyticsFilter(AnalyticsFilterTypesEnum.LOCALE);
  const [startDate, endDate] = useAppSelector(getAnalyticsDatetimes);
  const [selectedSidePanelMetricType, setSelectedSidePanelMetricType] =
    useState<KnowledgeBaseReportSidePanelSupportTypes>();
  const [selectedSidePanelItem, setSelectedSidePanelItem] =
    useState<KnowledgeBaseReportSidePanelSelectedItem>();

  useInitialEffect(() => {
    const startTime = startDate?.toMillis();
    const endTime = endDate?.toMillis();
    trackAnalyticsFrontendEvent(DataEventActionsEnum.VIEW, AnalyticsDataEventObject.REPORT, {
      start_time: startTime,
      end_time: endTime,
    });

    // TODO: move this once we add view support
    trackAnalyticsFrontendEvent(DataEventActionsEnum.VIEW, AnalyticsDataEventObject.VIEW, {
      start_time: startTime,
      end_time: endTime,
    });

    // On unmount, clear the KB filters.
    return () => {
      dispatch(updateFiltersAction({[AnalyticsFilterTypesEnum.KNOWLEDGE_BASES]: []}));
    };
  });

  const isMetricsEmpty = useAppSelector(
    makeGetShouldRenderReportEmptyState([
      // Hero Cards
      AnalyticsScalarMetricsEnum.OVERVIEW_NUM_TOTAL_ARTICLE_VIEWS,
      AnalyticsScalarMetricsEnum.OVERVIEW_NUM_TOTAL_UNIQUE_VIEWS,
      AnalyticsScalarMetricsEnum.OVERVIEW_NUM_TOTAL_VIEWS,
      AnalyticsScalarMetricsEnum.OVERVIEW_NUM_ARTICLE_UPDATES,
      // Graphs
      AnalyticsGraphMetricsEnum.PAGE_VIEW_NUM_TOTAL_TOTAL_VIEWS,
      AnalyticsGraphMetricsEnum.PAGE_VIEW_NUM_TOTAL_UNIQUE_VIEWS,
      AnalyticsGraphMetricsEnum.PAGE_VIEW_NUM_TOTAL_UNIQUE_ARTICLE_VIEWS,
      AnalyticsGraphMetricsEnum.PAGE_VIEW_NUM_ARTICLE_UPDATES,
    ]),
  );
  const shouldRenderReportEmptyState = !selectedKnowledgeBaseIds?.length || isMetricsEmpty;
  const knowledgeBaseId = selectedKnowledgeBaseIds?.[0];
  const selectedLocale = selectedLocales?.[0];

  const perfGraphSeries: Array<ChartSerie<AnalyticsGraphMetricValue>> = useMemo(
    () => [
      {
        accessor: (d) => d?.value,
        label: '', // Label will be set dynamically based on the selected metric.
        color: theme.palette.blue.shade40,
      },
    ],
    [theme.palette.blue.shade40],
  );

  const {selectedMetricIds} = useAnalyticsSelectedMetrics<AnalyticsScalarMetricsEnum>(
    ReportElementIdsEnum.KNOWLEDGE_BASE_METRIC_CARDS,
  );
  const isMetricLoading = useAppSelector(getAnalyticsMetricIsLoading);
  const areAllKeyMetricsEmpty = useAppSelector(makeAreScalarMetricsEmpty(selectedMetricIds));

  const renderContent = () => {
    if (shouldRenderReportEmptyState && !isLoading) {
      return <KnowledgeBaseReportEmptyState />;
    }
    return (
      <StyledReportGridWrapperDiv>
        <StyledReportGrid>
          <StyledReportGridLayoutDiv>
            <StyledReportGridLayoutItemDiv $width={2}>
              <StyledReportSection
                isDisabled={isLoading}
                reportSectionId={ReportElementIdsEnum.KNOWLEDGE_BASE_METRIC_CARDS}
                title={<Trans id="analytics-knowledge-base-key-metrics-title">Key metrics</Trans>}
                hasMetricMultiselect
                minHeight={reportSectionBaseHeight}
                sectionTooltip={{
                  title: <Trans id="analytics-knowledge-base-key-metrics-title">Key metrics</Trans>,
                  tooltipContent: <HeroMetricSubtitle />,
                  learnMoreUrl: `https://help.front.com/en/articles/696960`,
                }}
              >
                {areAllKeyMetricsEmpty && <ReportSectionEmptyState />}
                {!areAllKeyMetricsEmpty &&
                  selectedMetricIds.map((metricId) => (
                    <ReportKeyMetric
                      isLoading={isMetricLoading(metricId)}
                      key={metricId}
                      metricId={metricId}
                      reportSectionId={ReportElementIdsEnum.KNOWLEDGE_BASE_METRIC_CARDS}
                      onClickCustom={(mId) => {
                        if (mId === AnalyticsScalarMetricsEnum.OVERVIEW_NUM_ARTICLE_UPDATES) {
                          dispatch(openKnowledgeBaseAnalyticsSidePanelAction());
                          setSelectedSidePanelMetricType(mId);
                        }
                      }}
                    />
                  ))}
              </StyledReportSection>
            </StyledReportGridLayoutItemDiv>
            <StyledReportGridLayoutItemDiv $width={4}>
              <ReportLineChartGridFrame>
                <ReportLineChart
                  reportSectionId={ReportElementIdsEnum.KNOWLEDGE_BASE_CHANGES_OVER_TIME}
                  title={localize(
                    defineMessage({
                      id: 'analytics-knowledge-base-report-changes-over-time-title',
                      message: 'Changes over time',
                    }),
                  )}
                  isLoading={isLoading}
                  availableMetrics={[
                    AnalyticsGraphMetricsEnum.PAGE_VIEW_NUM_TOTAL_TOTAL_VIEWS,
                    AnalyticsGraphMetricsEnum.PAGE_VIEW_NUM_TOTAL_UNIQUE_VIEWS,
                    AnalyticsGraphMetricsEnum.PAGE_VIEW_NUM_TOTAL_UNIQUE_ARTICLE_VIEWS,
                    AnalyticsGraphMetricsEnum.PAGE_VIEW_NUM_ARTICLE_UPDATES,
                  ]}
                  series={perfGraphSeries}
                  xAccessor={(d: AnalyticsGraphMetricValue) => d.label}
                  shouldRenderOnlyWholeNumbers
                  height={220}
                />
              </ReportLineChartGridFrame>
            </StyledReportGridLayoutItemDiv>
            <StyledReportGridLayoutItemDiv $width={6}>
              <TableGridFrame>
                <ReportTable
                  reportSectionId={ReportElementIdsEnum.KNOWLEDGE_BASE_ENGAGEMENT_TABLE}
                  metric={AnalyticsTableMetricsEnum.ARTICLE_PAGE_VIEWS}
                  defaultColumnIds={engagementTableColumnIds}
                  isLoading={isLoading}
                  disableSelection
                  shouldAllowFirstColumnSelection
                  onClickCustom={(metric, cell) => {
                    dispatch(openKnowledgeBaseAnalyticsSidePanelAction());
                    if (metric !== AnalyticsTableMetricsEnum.ARTICLE_PAGE_VIEWS) {
                      return;
                    }
                    setSelectedSidePanelMetricType(metric);
                    if (cell.row.original.page_name.type === AnalyticsDataPointTypesEnum.PAGE_VIEW) {
                      setSelectedSidePanelItem({
                        pageType: cell.row.original.page_name.pageType,
                        id: cell.row.original.page_name.id,
                        locale: selectedLocale,
                      });
                    }
                  }}
                  initialState={{
                    sortBy: defaultEngagementSortBy,
                  }}
                />
              </TableGridFrame>
            </StyledReportGridLayoutItemDiv>
            <StyledReportGridLayoutItemDiv $width={6}>
              <TableGridFrame>
                <ReportTable
                  reportSectionId={ReportElementIdsEnum.KNOWLEDGE_BASE_SEARCH_TERMS_TABLE}
                  metric={AnalyticsTableMetricsEnum.SEARCH_TERMS}
                  defaultColumnIds={defaultSearchTermColumnIds}
                  isLoading={isLoading}
                  disableSelection
                  shouldDisableClick
                  initialState={{
                    sortBy: defaultSearchSortBy,
                  }}
                />
              </TableGridFrame>
            </StyledReportGridLayoutItemDiv>
          </StyledReportGridLayoutDiv>
        </StyledReportGrid>
      </StyledReportGridWrapperDiv>
    );
  };

  return (
    <>
      <StyledReportViewerWrapperDiv>
        <KnowledgeBaseReportHeader panelId={panelId} />
        <ErrorBoundary
          // eslint-disable-next-line react/no-unstable-nested-components
          fallbackRender={(error) => <KnowledgeBaseReportErrorBoundary error={error} panelId={panelId} />}
          onReset={() => dispatch(fetchVisibleMetricsForReport())}
        >
          {renderContent()}
        </ErrorBoundary>
      </StyledReportViewerWrapperDiv>
      <ReportSidePanel
        isVisible={isVisible}
        isResizable={false}
        initialWidth={400}
        renderCustomSidePanel={(onRequestClose) => {
          if (!knowledgeBaseId) {
            return null;
          }

          return (
            <KnowledgeBaseReportSidePanel
              knowledgeBaseId={knowledgeBaseId}
              selectedMetricType={selectedSidePanelMetricType}
              selectedItem={selectedSidePanelItem}
              onRequestClose={() => {
                setSelectedSidePanelMetricType(undefined);
                setSelectedSidePanelItem(undefined);
                onRequestClose();
              }}
              onArticleSelect={({articleId, shouldDisplayDraft}) => {
                setSelectedSidePanelItem({
                  id: articleId,
                  pageType: AnalyticsDataPointPageViewPageTypesEnum.ARTICLE,
                  locale: selectedLocale,
                  shouldDisplayDraft,
                });
              }}
              onClearSelectedItem={() => setSelectedSidePanelItem(undefined)}
            />
          );
        }}
      />
    </>
  );
};
